import React, { useState, useEffect } from 'react';
import { useTheme } from '../../context/PortalThemeContext';
import { ClaimStates } from './ClaimStates';
import AttachImageOption from '../shared/AttachImageOption';
import NavButtons from '../returns/NavButtons';
import { fetchWithSessionToken } from '../shared/fetchWithSessionToken';
import { fallbackString } from '../shared/FallbackUtils';
import { CHAT_API_BASE } from '../../config/api';
import { PiPackage, PiWarning, PiArrowsClockwise, PiShieldWarning, PiQuestion } from 'react-icons/pi';
import './ClaimReasonSelection.css';

const REASON_ICONS = {
    missing:   { Icon: PiPackage,           desc: 'Package never arrived or is lost' },
    lost:      { Icon: PiPackage,           desc: 'Package never arrived or is lost' },
    damaged:   { Icon: PiWarning,           desc: 'Product arrived damaged during shipping' },
    defective: { Icon: PiWarning,           desc: 'Product arrived defective or non-functional' },
    wrong:     { Icon: PiArrowsClockwise,   desc: 'Received a different item than ordered' },
    stolen:    { Icon: PiShieldWarning,     desc: 'Package was stolen from delivery location' },
};

function getReasonMeta(name) {
    const lower = (name || '').toLowerCase();
    for (const [key, meta] of Object.entries(REASON_ICONS)) {
        if (lower.includes(key)) return meta;
    }
    return { Icon: PiQuestion, desc: '' };
}

const ClaimReasonSelection = ({
    onReasonSelected,
    onStepChange,
    flowType = 'CLAIM'
}) => {
    const theme = useTheme();

    const [claimFlow, setClaimFlow] = useState(null);
    const [navStack, setNavStack] = useState([]);
    const [selectedReason, setSelectedReason] = useState(null);
    const [storedReasons, setStoredReasons] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [loadingError, setLoadingError] = useState(null);
    const [error, setError] = useState(false);

    function getState(toStateId) {
        return claimFlow?.states?.find(state => state.stateId === toStateId);
    }

    useEffect(() => {
        const getClaimFlow = async () => {
            setIsLoading(true);
            setLoadingError(null);

            const shopId = theme.store;

            try {
                const url = new URL(`${CHAT_API_BASE}/workflow/${shopId}/claim_flow`);

                const response = await fetchWithSessionToken(url.toString(), {
                    method: 'GET',
                    headers: {
                        'Content-Type': 'application/json',
                    },
                });

                if (!response.ok) {
                    setLoadingError('No options available. Please try again later.');
                    setIsLoading(false);
                    return;
                }

                const claimFlowJson = await response.json();
                const claimFlow = {
                    shopId: claimFlowJson.shopId,
                    workflowId: claimFlowJson.workflowId,
                    name: claimFlowJson.name,
                    startState: claimFlowJson.startState,
                    states: (claimFlowJson.states || []).map((state) => ({
                        stateId: state.stateId,
                        name: state.name,
                        title: state.title,
                        type: state.type,
                        actionCode: state.actionCode,
                        transitions: (state.transitions || []).map((transition) => ({
                            name: transition.name,
                            toStateId: transition.toStateId,
                            showOptionalText: transition.showOptionalText,
                            allowImageUpload: transition.allowImageUpload,
                            imageUrl: transition.imageUrl,
                        })),
                        isFinal: state.isFinal
                    }))
                };

                setClaimFlow(claimFlow);
                setNavStack([claimFlow.startState]);
                setIsLoading(false);
            } catch (error) {
                console.error('Error fetching claim flow:', error);
                setLoadingError('Failed to load claim options. Please try again.');
                setIsLoading(false);
            }
        };

        getClaimFlow();
    }, [theme.store]);

    useEffect(() => {
        if (claimFlow && navStack.length > 0) {
            const currentState = getState(navStack[navStack.length - 1]);

            // Handle both 'QUESTION' and 'workflowSelection' types
            if (currentState && (currentState.type === 'QUESTION' || currentState.type === 'workflowSelection')) {
                setStoredReasons(currentState.transitions || []);
            } else {
                // Fallback: if we have a current state with transitions, use them anyway
                if (currentState && currentState.transitions && currentState.transitions.length > 0) {
                    setStoredReasons(currentState.transitions);
                }
            }
        }
    }, [claimFlow, navStack]);

    const handleReasonSelect = (reason) => {
        setSelectedReason(reason);
    };

    const handleNext = () => {
        if (!selectedReason) {
            setError(true);
            return;
        }

        onReasonSelected(selectedReason);
    };

    const handleBack = () => {
        onStepChange(ClaimStates.ORDER_SUMMARY);
    };



    if (isLoading) {
        return (
            <div className="text-center p-4">
                <div className="spinner-border" role="status">
                    <span className="visually-hidden">{'Loading...'}</span>
                </div>
            </div>
        );
    }

    const currentState = navStack.length > 0 ? getState(navStack[navStack.length - 1]) : null;
    const title = currentState?.title || 'Choose Claim Reason';

    return (
        <div className="claim-reason-selection">
            <div className="claim-reason-header">
                <h2>{fallbackString(title, 'What happened to your order?')}</h2>
                <p>Select the reason that best describes your issue</p>
            </div>

            <div className="claim-reasons-container">
                {storedReasons.length > 0 ? (
                    storedReasons.map((t, index) => {
                        const isSelected = selectedReason?.name === t.name;
                        const transition = {
                            ...t,
                            optionalText: isSelected ? selectedReason.optionalText : '',
                            images: isSelected ? selectedReason.images : [],
                        };
                        const { Icon, desc } = getReasonMeta(t.name);

                        return (
                            <div key={index}>
                                <div
                                    className={`claim-reason-card ${isSelected ? 'selected' : ''}`}
                                    onClick={() => handleReasonSelect(transition)}
                                >
                                    <div className={`claim-reason-icon ${isSelected ? 'selected' : ''}`}>
                                        <Icon size={20} />
                                    </div>
                                    <div className="claim-reason-text">
                                        <h3>{t.name}</h3>
                                        {desc && <p>{desc}</p>}
                                    </div>
                                </div>

                                {isSelected && (transition.showOptionalText || transition.allowImageUpload) && (
                                    <div className="claim-reason-extras">
                                        {transition.showOptionalText && (
                                            <textarea
                                                value={transition.optionalText ?? ''}
                                                onChange={(e) => {
                                                    transition.optionalText = e.target.value;
                                                    setSelectedReason(transition);
                                                    setError(false);
                                                }}
                                                placeholder="Describe your issue (optional)"
                                                rows={3}
                                                style={{ resize: 'vertical' }}
                                                className="form-control"
                                            />
                                        )}
                                        {selectedReason.allowImageUpload && (
                                            <AttachImageOption
                                                uploadedImages={selectedReason.images || []}
                                                setUploadedImages={(uploads) => setSelectedReason({ ...selectedReason, images: uploads })}
                                            />
                                        )}
                                    </div>
                                )}
                            </div>
                        );
                    })
                ) : (
                    <div className="text-center p-4">
                        <p>No claim reasons available.</p>
                    </div>
                )}
            </div>

            <NavButtons
                handleBack={handleBack}
                handleNext={handleNext}
                disabled={!selectedReason}
                onDisabledClick={() => setError(true)}
            />
        </div>
    );
};

export default ClaimReasonSelection;
